import { formatMoney } from '../../core/math';
import { DIFFICULTIES } from '../../game/config';
import type { DifficultyId } from '../../game/types';
import { ExteriorRenderer } from '../../render/exterior';
import { h, onPress, setText } from '../dom';

export interface TitleActions {
  daily(): void;
  free(difficulty: DifficultyId): void;
  fight(): void;
  howTo(): void;
  achievements(): void;
  settings(): void;
}

const TAGLINES = [
  'Keep the Waffle House Index green.',
  'Scattered. Smothered. Covered.',
  'The doors don’t lock. We never close.',
  'If a chair comes flying… catch it.',
  'It’s always 3 AM somewhere.',
  'FEMA is watching.',
  'Sir, this is a Waffle House.',
];

export function tileRow(word: string, className = 'tile'): HTMLElement {
  return h(
    'div',
    { class: 'tiles', 'aria-hidden': 'true' },
    ...[...word].map((ch, i) =>
      h(
        'span',
        { class: `${className}${ch === ' ' ? ' is-space' : ''}`, style: `--i:${i}` },
        ch === ' ' ? '' : ch,
      ),
    ),
  );
}

export class TitleScreen {
  readonly el: HTMLElement;
  private readonly exterior: ExteriorRenderer;
  private readonly dailyButton: HTMLButtonElement;
  private readonly dailySub: HTMLElement;
  private readonly record: HTMLElement;
  private readonly tagline: HTMLElement;
  private readonly picker: HTMLElement;
  private taglineIndex = 0;
  private taglineTimer = 0;

  constructor(actions: TitleActions) {
    const canvas = h('canvas', { class: 'title__bg', 'aria-hidden': 'true' });
    this.exterior = new ExteriorRenderer(canvas);
    this.dailySub = h('small', null, 'DAILY SHIFT');
    this.dailyButton = h(
      'button',
      { class: 'cta', type: 'button' },
      h('span', null, 'CLOCK IN'),
      this.dailySub,
    );
    onPress(this.dailyButton, () => actions.daily());
    this.record = h('div', { class: 'title__record' });
    this.tagline = h('p', { class: 'title__tagline' }, TAGLINES[0]);

    const freeButton = h(
      'button',
      { class: 'btn-secondary', type: 'button', 'aria-expanded': 'false' },
      'FREE PLAY ▾',
    );
    this.picker = h(
      'div',
      { class: 'picker', hidden: true },
      ...(Object.keys(DIFFICULTIES) as DifficultyId[]).map((id) => {
        const d = DIFFICULTIES[id];
        const b = h(
          'button',
          { class: `picker__opt picker__opt--${id}`, type: 'button' },
          h('b', null, d.label),
          h('span', null, d.blurb),
        );
        onPress(b, () => actions.free(id));
        return b;
      }),
      (() => {
        const b = h(
          'button',
          { class: 'picker__opt picker__opt--fight', type: 'button' },
          h('b', null, 'CALM THE CROWD 🥊'),
          h('span', null, 'Just the shift-change fights. They also happen between every shift.'),
        );
        onPress(b, () => actions.fight());
        return b;
      })(),
    );
    onPress(freeButton, () => {
      const open = this.picker.hidden;
      this.picker.hidden = !open;
      freeButton.setAttribute('aria-expanded', String(open));
    });

    const small = (label: string, fn: () => void): HTMLButtonElement => {
      const b = h('button', { class: 'btn-ghost', type: 'button' }, label);
      onPress(b, fn);
      return b;
    };

    this.el = h(
      'section',
      { class: 'screen screen--title', 'aria-label': '3AM Shift title screen' },
      canvas,
      h('div', { class: 'title__shade', 'aria-hidden': 'true' }),
      h(
        'div',
        { class: 'title__content' },
        h('h1', { class: 'logo', 'aria-label': '3AM Shift' }, tileRow('3AM'), tileRow('SHIFT')),
        h('p', { class: 'title__sub' }, 'an unofficial Waffle House night-shift simulator'),
        this.tagline,
        h(
          'div',
          { class: 'title__menu' },
          this.dailyButton,
          this.record,
          freeButton,
          this.picker,
          h(
            'div',
            { class: 'title__row' },
            small('HOW TO PLAY', actions.howTo),
            small('ACHIEVEMENTS', actions.achievements),
            small('SETTINGS', actions.settings),
          ),
        ),
        h(
          'footer',
          { class: 'title__legal' },
          'Unofficial fan parody. Not affiliated with or endorsed by Waffle House, Inc. · ',
          h(
            'a',
            {
              href: 'https://github.com/caseyrmorrison/waffle-house-sim',
              target: '_blank',
              rel: 'noopener',
            },
            'source',
          ),
        ),
      ),
    );
  }

  setDaily(
    shiftNumber: number,
    best: { tips: number; closed: boolean; share: string } | undefined,
    streakNote: string,
  ): void {
    setText(this.dailySub, `DAILY SHIFT #${shiftNumber}`);
    if (best) {
      const strip =
        best.share
          .split('\n')
          .find((l) => l.startsWith('Waffle House Index:'))
          ?.replace('Waffle House Index: ', '') ?? '';
      setText(this.record, `Today: ${best.closed ? 'CLOSED 🟥' : formatMoney(best.tips)} ${strip}`);
    } else {
      setText(this.record, streakNote);
    }
  }

  resize(): void {
    this.exterior.resize();
  }

  setReducedMotion(on: boolean): void {
    this.exterior.reducedMotion = on;
  }

  frame(dt: number): void {
    this.exterior.render(dt);
    this.taglineTimer += dt;
    if (this.taglineTimer > 3.6) {
      this.taglineTimer = 0;
      this.taglineIndex = (this.taglineIndex + 1) % TAGLINES.length;
      this.tagline.classList.remove('is-in');
      void this.tagline.offsetWidth;
      this.tagline.textContent = TAGLINES[this.taglineIndex] ?? '';
      this.tagline.classList.add('is-in');
    }
  }
}
